<template>
  <div class="vr">
    <button v-if="!recording" type="button" class="vr-mic" :class="{ 'vr-mic-sm': small }" :disabled="disabled"
            :title="label" :aria-label="label" @click="start">
      <i class="fa fa-microphone"></i>
      <span v-if="!small">{{ label }}</span>
    </button>
    <div v-else class="vr-live" role="status">
      <span class="vr-dot"></span>
      <bdi dir="ltr" class="vr-time">{{ clock }}</bdi>
      <span class="vr-hint">يسجّل الآن</span>
      <button type="button" class="vr-btn vr-cancel" title="حذف التسجيل" aria-label="حذف التسجيل" @click="cancel">
        <i class="fa fa-trash"></i>
      </button>
      <button type="button" class="vr-btn vr-stop" title="إنهاء التسجيل" aria-label="إنهاء التسجيل" @click="stop">
        <i class="fa fa-check"></i>
      </button>
    </div>
    <div v-if="error" class="vr-error">{{ error }}</div>
  </div>
</template>

<script>
import { MAX_VOICE_SECONDS, seconds } from './support-api';

// Records one voice note in the browser, in a format the iPhone app can also play (it can't play
// the WebM / Ogg that Chrome and Firefox record by default):
// - AAC in MP4 (.m4a) where MediaRecorder can (Safari, recent Chrome);
// - otherwise the raw microphone samples, saved as WAV (mono, 16 kHz, 16-bit: 3 min ≈ 5.8 MB).
// Both are accepted by /user/support/upload. Emits 'recorded' { blob, name, duration }.
var WAV_RATE = 16000;

function aacType(){
  if (typeof MediaRecorder == 'undefined' || !MediaRecorder.isTypeSupported) return null;
  var c = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4;codecs="mp4a.40.2"'];
  for (var i = 0; i < c.length; i++) if (MediaRecorder.isTypeSupported(c[i])) return c[i];
  // Safari only records MP4 with AAC (it has no WebM recorder)
  if (MediaRecorder.isTypeSupported('audio/mp4') && !MediaRecorder.isTypeSupported('audio/webm')) return 'audio/mp4';
  return null;
}

function audioContextClass(){
  return typeof window != 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
}

// Float32 chunks at `rate` → 16-bit mono WAV at WAV_RATE (averaging each window when downsampling).
function encodeWav(chunks, rate){
  var total = 0, i, j;
  for (i = 0; i < chunks.length; i++) total += chunks[i].length;
  var all = new Float32Array(total), at = 0;
  for (i = 0; i < chunks.length; i++) { all.set(chunks[i], at); at += chunks[i].length; }
  var ratio = rate > WAV_RATE ? rate / WAV_RATE : 1, outRate = rate > WAV_RATE ? WAV_RATE : rate;
  var n = Math.floor(total / ratio), view = new DataView(new ArrayBuffer(44 + n * 2));
  function str(o, t){ for (var k = 0; k < t.length; k++) view.setUint8(o + k, t.charCodeAt(k)); }
  str(0, 'RIFF'); view.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, outRate, true); view.setUint32(28, outRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  str(36, 'data'); view.setUint32(40, n * 2, true);
  for (i = 0; i < n; i++) {
    var a = Math.floor(i * ratio), b = Math.min(total, Math.floor((i + 1) * ratio)), sum = 0, cnt = 0;
    for (j = a; j < b; j++) { sum += all[j]; cnt++; }
    var v = Math.max(-1, Math.min(1, cnt ? sum / cnt : 0));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([view], { type: 'audio/wav' });
}

export default {
  props: {
    small: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: 'تسجيل رسالة صوتية' },
  },
  data(){
    return { recording: false, elapsed: 0, error: '', rec: null, stream: null, chunks: [], timer: null, started: 0, discard: false,
      ctx: null, src: null, proc: null, pcm: [], rate: 0 };
  },
  computed: {
    clock(){ return seconds(this.elapsed); },
  },
  beforeDestroy(){
    this.discard = true;
    this.release();
  },
  methods: {
    async start(){
      this.error = '';
      var aac = aacType(), Ctx = audioContextClass();
      if ((!aac && !Ctx) || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = 'المتصفح لا يدعم تسجيل الصوت، استخدم متصفح كروم أو أرفق ملفاً صوتياً.';
        return;
      }
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        this.error = 'لم يُسمح بالوصول للميكروفون. فعّله من إعدادات المتصفح ثم حاول مرة أخرى.';
        return;
      }
      var g = this;
      g.discard = false;
      try {
        if (aac) g.startAac(aac);
        else await g.startWav(Ctx);
      } catch (e) {
        g.discard = true;
        g.release();
        g.error = 'تعذّر بدء التسجيل، حاول مرة أخرى.';
        return;
      }
      g.started = Date.now();
      g.elapsed = 0;
      g.recording = true;
      g.$emit('recording', true);
      g.timer = setInterval(function(){
        g.elapsed = Math.round((Date.now() - g.started) / 1000);
        if (g.elapsed >= MAX_VOICE_SECONDS) g.stop();
      }, 250);
    },
    startAac(type){
      var g = this;
      g.chunks = [];
      g.rec = new MediaRecorder(g.stream, { mimeType: type });
      g.rec.ondataavailable = function(e){ if (e.data && e.data.size) g.chunks.push(e.data); };
      g.rec.onstop = function(){
        var chunks = g.chunks;
        g.finish(chunks.length ? new Blob(chunks, { type: 'audio/mp4' }) : null, 'm4a');
      };
      g.rec.start(1000);
    },
    async startWav(Ctx){
      var g = this;
      g.pcm = [];
      g.ctx = new Ctx();
      if (g.ctx.state == 'suspended') await g.ctx.resume();
      g.rate = g.ctx.sampleRate;
      g.src = g.ctx.createMediaStreamSource(g.stream);
      g.proc = g.ctx.createScriptProcessor(4096, 1, 1);
      g.proc.onaudioprocess = function(e){
        if (g.recording) g.pcm.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      g.src.connect(g.proc);
      // the processor only runs while connected to an output; it writes silence
      g.proc.connect(g.ctx.destination);
    },
    finish(blob, ext){
      var duration = Math.max(1, Math.round((Date.now() - this.started) / 1000));
      var discard = this.discard;
      this.release();
      if (discard || !blob || !blob.size) return;
      this.$emit('recorded', { blob: blob, name: 'voice-' + Date.now() + '.' + ext, duration: duration });
    },
    stop(){
      if (this.rec && this.rec.state != 'inactive') {
        this.rec.stop();
      } else if (this.proc) {
        var pcm = this.pcm, rate = this.rate;
        this.finish(pcm.length ? encodeWav(pcm, rate) : null, 'wav');
      } else {
        this.release();
      }
    },
    cancel(){
      this.discard = true;
      this.stop();
    },
    release(){
      clearInterval(this.timer);
      this.timer = null;
      if (this.proc) { try { this.proc.disconnect(); } catch (e) {} this.proc.onaudioprocess = null; }
      if (this.src) { try { this.src.disconnect(); } catch (e) {} }
      if (this.ctx) { try { this.ctx.close(); } catch (e) {} }
      this.proc = null; this.src = null; this.ctx = null; this.pcm = [];
      this.rec = null;
      if (this.stream) this.stream.getTracks().forEach(function(x){ x.stop(); });
      this.stream = null;
      if (this.recording) this.$emit('recording', false);
      this.recording = false;
    },
  },
};
</script>

<style scoped>
.vr { display: inline-flex; flex-direction: column; gap: 6px; min-width: 0; }
.vr-mic {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  height: 42px; padding: 0 14px; border-radius: 12px; border: 1px solid rgba(127, 127, 127, 0.28);
  background: transparent; color: inherit; font-weight: 600; cursor: pointer;
}
.vr-mic:hover:not(:disabled) { background: rgba(127, 127, 127, 0.08); }
.vr-mic:disabled { opacity: 0.5; cursor: not-allowed; }
.vr-mic-sm { width: 42px; padding: 0; background: var(--primary); border-color: var(--primary); color: #fff; }
.vr-mic-sm:hover:not(:disabled) { background: var(--primary); filter: brightness(1.08); }
.vr-live {
  display: inline-flex; align-items: center; gap: 10px; height: 42px; padding: 0 6px 0 12px;
  border-radius: 12px; background: rgba(234, 84, 85, 0.1); color: #c62828;
}
.vr-dot { width: 10px; height: 10px; border-radius: 50%; background: #ea5455; animation: vr-pulse 1s ease-in-out infinite; }
.vr-time { font-weight: 700; font-variant-numeric: tabular-nums; }
.vr-hint { font-size: 12px; }
.vr-btn { width: 32px; height: 32px; border-radius: 9px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.vr-cancel { background: transparent; color: #c62828; }
.vr-stop { background: #ea5455; color: #fff; }
.vr-error { font-size: 12px; color: #ea5455; max-width: 320px; }
@keyframes vr-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@media (prefers-reduced-motion: reduce) { .vr-dot { animation: none; } }
</style>
